/**
 * phase2CapacityShared.ts
 * Shared constants, day helpers, and the EmployeeRow type for the merged
 * "Team Allocation & Weekly Capacity" grid (Phase2CapacityTable + its extracted
 * row / cell / add-person components).
 *
 * Column geometry derives from the SHARED rail tokens (phase2RailGeometry) so
 * this grid stays lined up week-for-week with the Task Weekly Schedule above it:
 * gutter = Employee (EMP_COL_W) + Role (ROLE_COL_W) === RAIL_GUTTER_W, and each
 * day column === RAIL_DAY_W. Keep them in sync there.
 */

import { RAIL_DAY_W } from './phase2RailGeometry';

/** Employee-name gutter column width. */
export const EMP_COL_W = 180;
/** Role gutter column width. EMP_COL_W + ROLE_COL_W === RAIL_GUTTER_W. */
export const ROLE_COL_W = 100;
/** One day column — must equal the rail's day width so weeks align. */
export const DAY_W = RAIL_DAY_W;
/** One week = 7 day columns. */
export const WEEK_W = DAY_W * 7;

// BST week is Saturday→Friday, so day 0 = Sat, 1 = Sun (weekend), 2–6 = Mon–Fri.
export const DAY_LABELS = ['Sa', 'Su', 'Mo', 'Tu', 'We', 'Th', 'Fr'] as const;

/** Add n days to an ISO date → 'YYYY-MM-DD'. */
export function addDaysISO(dateStr: string, n: number): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}

/** The 7 ISO day dates (Sat→Fri) of a BST week. */
export function weekDays(weekStart: string): string[] {
  return Array.from({ length: 7 }, (_, i) => addDaysISO(weekStart, i));
}

/** One resolved employee row — one per distinct user in the plan (assigned or rostered). */
export interface EmployeeRow {
  userKey: string;
  displayName: string;
  role: string | null;
  /** AppUser email when known (roster-only members, 2026-07-28) — photo lookup. */
  email?: string | null;
}
